/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import { useState, useEffect } from 'react';
import { getFirestore, doc, onSnapshot } from 'firebase/firestore';

interface SyncMetadata {
  last_sync: any;
  orders_synced?: number;
  sync_type?: string;
}

export default function SalesDataLastUpdated() {
  const [shopifySync, setShopifySync] = useState<SyncMetadata | null>(null);
  const [printfulSync, setPrintfulSync] = useState<SyncMetadata | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const db = getFirestore();
    
    console.log('Setting up sales data sync listeners...');
    
    // Listen to Shopify sync status
    const unsubscribeShopify = onSnapshot(
      doc(db, 'sync_metadata', 'shopify'),
      (doc) => {
        console.log('Shopify sync updated');
        if (doc.exists()) {
          setShopifySync(doc.data() as SyncMetadata);
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error listening to Shopify sync:', error);
        setLoading(false);
      }
    );

    // Listen to Printful sync status
    const unsubscribePrintful = onSnapshot(
      doc(db, 'sync_metadata', 'printful'),
      (doc) => {
        console.log('Printful sync updated');
        if (doc.exists()) {
          setPrintfulSync(doc.data() as SyncMetadata);
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error listening to Printful sync:', error);
        setLoading(false);
      }
    );

    // Cleanup both listeners on unmount
    return () => {
      unsubscribeShopify();
      unsubscribePrintful();
    };
  }, []);

  const getLatestTimestamp = () => {
    const shopifyTime = shopifySync?.last_sync;
    const printfulTime = printfulSync?.last_sync;

    if (!shopifyTime && !printfulTime) {
      return null;
    }

    if (!shopifyTime) return printfulTime;
    if (!printfulTime) return shopifyTime;

    // Convert both to dates for comparison
    let shopifyDate;
    if (shopifyTime.toDate) {
      shopifyDate = shopifyTime.toDate();
    } else if (shopifyTime.seconds) {
      shopifyDate = new Date(shopifyTime.seconds * 1000);
    } else {
      shopifyDate = new Date(shopifyTime);
    }

    let printfulDate;
    if (printfulTime.toDate) {
      printfulDate = printfulTime.toDate();
    } else if (printfulTime.seconds) {
      printfulDate = new Date(printfulTime.seconds * 1000);
    } else {
      printfulDate = new Date(printfulTime);
    }

    // Return the later of the two
    return shopifyDate > printfulDate ? shopifyTime : printfulTime;
  };

  const formatTimestamp = (timestamp: any) => {
    if (!timestamp) {
      return 'Never';
    }
    
    let date;
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else {
      date = new Date(timestamp);
    }
    
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div style={{
        padding: '5px 20px',
        backgroundColor: '#f8f9fa',
        border: '1px solid #dee2e6',
        borderRadius: '4px',
        fontSize: '12px',
        color: '#6c757d',
        marginBottom: '10px',
        display:'inline',
      }}>
        Loading sync status...
      </div>
    );
  }

  const latestTimestamp = getLatestTimestamp();

  return (
    <div style={{fontSize:'.8em',padding:'10px'}}>
      <strong>Updated:</strong> {formatTimestamp(latestTimestamp)}
    </div>
  );
}